/* eslint-disable no-console */
/* eslint-disable arrow-parens */
import React, { useState } from 'react'
import { useStripe, useElements, CardElement } from '@stripe/react-stripe-js'
import { CountryDropdown, RegionDropdown } from 'react-country-region-selector'
import { SmallInput as Input } from '@/style-guide'
import { Loader } from '@/components'
import CardSection from './CardSection'
import { saveCard, createCardIntent } from '@/utils/apiEndpoints'
import { Button, Form } from 'antd'
import { useSelector } from 'react-redux'
import { StripeAttribution } from './StripeAttribution'
import common from '@/utils/common'

const styles = {
  select: {
    backgroundColor: 'white',
    color: '#32325d',
    fontSize: 14,
    fontFamily: '"Roboto", system-ui',
    borderRadius: 5,
    padding: '10px 12px',
    outline: 'none',
    margin: '0',
    width: '100%',
  },
}

const CardInputForm = ({
  onCardAddSuccess,
  onCardAddCancel,
  hideCancel,
  okButtonText = 'Authorize',
}) => {
  const [form] = Form.useForm()
  const { currentUser } = useSelector((state) => state.auth)
  const { stripeCustomerId, email } = currentUser
  const [country, setCountry] = useState('US')
  const [loading, setLoading] = useState(false)
  const stripe = useStripe()
  const elements = useElements()
  const showError = (msg) => {
    common.errorToast(msg)
    return false
  }
  const getSecret = async (retry = 0) => {
    try {
      const resp = await createCardIntent()
      return resp.result
    } catch (err) {
      if (retry < 5) {
        return getSecret(retry + 1)
      } else {
        showError(
          'Something went wrong, undable to connect with payment gateway, try after some time.'
        )
      }
    }
  }

  const handleSubmit = async (values) => {
    if (!stripe || !elements) return showError('Please enter card details')

    if (!email) throw new showError('Missing user email.')
    try {
      setLoading(true)
      const secret = await getSecret()
      const result = await stripe.confirmCardSetup(secret, {
        payment_method: {
          card: elements.getElement(CardElement),
          billing_details: {
            address: {
              city: values.city,
              state: values.region,
              country: values.country,
              line1: values.line1,
              line2: values.line2 || '',
            },
            name: `${values.firstName} ${values.lastName}`,
          },
        },
      })
      if (result.error) {
        showError(result.error.message)
      } else {
        const requestPayload = {
          paymentMethod: result.setupIntent.payment_method,
        }
        if (stripeCustomerId)
          requestPayload['stripeCustomerId'] = stripeCustomerId
        else requestPayload['email'] = email
        const resp = await saveCard(requestPayload)
        onCardAddSuccess(resp.result)
      }
      return {}
    } catch (err) {
      return showError(
        'Unable to complete this operation, try after some time.'
      )
    } finally {
      setLoading(false)
    }
  }

  return (
    <Loader loading={loading}>
      <Form
        onFinish={handleSubmit}
        form={form}
        initialValues={{ country: 'US' }}
      >
        <Form.Item
          name="name"
          rules={[{ required: true, message: 'Please enter card details!' }]}
        >
          <CardSection />
        </Form.Item>
        <Form.Item
          name="firstName"
          rules={[{ required: true, message: 'Please enter first name!' }]}
        >
          <Input label="First Name" />
        </Form.Item>
        <Form.Item
          name="lastName"
          rules={[{ required: true, message: 'Please enter last name!' }]}
        >
          <Input label="Last Name" />
        </Form.Item>
        <Form.Item
          name="line1"
          rules={[{ required: true, message: 'Please enter address!' }]}
        >
          <Input label="Address" />
        </Form.Item>
        <Form.Item name="line2">
          <Input label="Apartment, suite, etc. (optional)" />
        </Form.Item>
        <Form.Item
          name="city"
          rules={[{ required: true, message: 'Please enter city name!' }]}
        >
          <Input label="City" />
        </Form.Item>
        <Form.Item
          name="country"
          rules={[{ required: true, message: 'Please select country!' }]}
        >
          <CountryDropdown
            valueType="short"
            onChange={(val) => {
              setCountry(val)
              form.setFieldsValue('country', val)
            }}
            style={styles.select}
          />
        </Form.Item>
        <Form.Item
          name="region"
          rules={[{ required: true, message: 'Please select region!' }]}
        >
          <RegionDropdown
            country={country}
            countryValueType="short"
            disableWhenEmpty
            style={styles.select}
          />
        </Form.Item>
        <div style={{ display: 'flex', gap: '10px' }}>
          {(!hideCancel || stripeCustomerId) && (
            <Button block type="ghost" size="large" onClick={onCardAddCancel}>
              Cancel
            </Button>
          )}
          <Button
            type="primary"
            htmlType="submit"
            size="large"
            block
            disabled={!stripe || !elements || loading}
            fontSize={16}
          >
            {okButtonText}
          </Button>
        </div>
        <StripeAttribution />
      </Form>
    </Loader>
  )
}

export default CardInputForm
